import { useEffect, useState } from 'react';
import { CheckCircle2, FileWarning, GitBranch, RefreshCw } from 'lucide-react';
import { chamarApi, ErroApi } from '../../../nucleo/auth/cliente-api';
import { PageHeader } from '../../../ui/PageHeader';
import { EmptyState } from '../../../ui/EmptyState';
import { Alert, AlertDescription } from '../../../ui/base/alert';
import { Button } from '../../../ui/base/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../ui/base/dialog';

const NS = '/bff/v1/j12-s02-montar-rastreabilidade';
type Item = {
  evento_id: string;
  tabela_origem: string;
  registro_origem_id: string;
  documento_origem: string;
  versao_origem: string;
  valor: string;
  ocorrido_em: string;
};
type Resultado = {
  id: string;
  consulta_id: string;
  instante_corte: string;
  consulta_executada: string;
  versoes_fontes: string[];
  estado: 'COMPLETO' | 'INCOMPLETO' | 'DIVERGENTE';
  total: string | null;
  soma_independente: string;
  quantidade_itens: number;
  fontes_incompletas: string[];
  hash_reproduzivel: string;
  itens: Item[];
};
type Consulta = {
  id: string;
  periodo_inicio: string;
  periodo_fim: string;
  processo: string;
  nivel_detalhe: string;
  consulta_hash: string;
  resultado_id?: string;
  estado?: string;
};
type Estado = {
  fase: string;
  titulo: string;
  descricao: string;
  consultas: Consulta[];
  resultados: Resultado[];
  resultadoSelecionado: Resultado | null;
  origemSelecionada: Item | null;
  alertas: Array<{ tipo: string; mensagem: string }>;
  dialogos: { origem: { aberto: boolean } };
  permissoes: { montar: boolean; recalcular: boolean; navegarOrigem: boolean };
  resultadoOperacao?: { tipo: string; mensagem: string; hashCoincide?: boolean } | null;
};
type Envelope = {
  contract_version: string;
  screen: string;
  event: { verb: string; outcome: string; at: string };
  state: Estado;
};

export function MontarRastreabilidadePage() {
  const [estado, setEstado] = useState<Estado | null>(null),
    [erro, setErro] = useState<string | null>(null),
    [carregando, setCarregando] = useState(true),
    [enviando, setEnviando] = useState(false);
  async function verbo(nome: string, corpo: unknown = {}) {
    setEnviando(true);
    setErro(null);
    try {
      setEstado(
        (await chamarApi<Envelope>(`${NS}/verbs/${nome}`, { metodo: 'POST', corpo })).state,
      );
    } catch (e) {
      setErro(aviso(e));
    } finally {
      setEnviando(false);
    }
  }
  useEffect(() => {
    void chamarApi<Envelope>(`${NS}/state`)
      .then((r) => setEstado(r.state))
      .catch((e) => setErro(aviso(e)))
      .finally(() => setCarregando(false));
  }, []);
  if (carregando)
    return (
      <div role="status" className="py-16 text-center text-muted-foreground">
        Carregando rastreabilidade…
      </div>
    );
  if (!estado)
    return (
      <EmptyState
        variant="error"
        title="Não foi possível carregar a rastreabilidade"
        description={erro ?? 'Tente novamente.'}
      />
    );
  const selecionado = estado.resultadoSelecionado;
  return (
    <section className="min-w-0 w-full max-w-[calc(100vw-2rem)] overflow-hidden">
      <PageHeader title={estado.titulo} description={estado.descricao} />
      {estado.resultadoOperacao && (
        <Alert className="mb-4">
          <AlertDescription>{estado.resultadoOperacao.mensagem}</AlertDescription>
        </Alert>
      )}
      {estado.alertas.map((a, i) => (
        <Alert key={`${a.tipo}-${i}`} className="mb-4">
          <AlertDescription>{a.mensagem}</AlertDescription>
        </Alert>
      ))}
      {erro && (
        <Alert className="mb-4">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <div className="grid content-start gap-3">
          <h2 className="font-semibold">Recortes disponíveis</h2>
          {estado.consultas.length === 0 ? (
            <EmptyState
              icon={GitBranch}
              title="Nenhum recorte disponível"
              description="Selecione um recorte autorizado na etapa anterior."
            />
          ) : (
            estado.consultas.map((c) => (
              <article key={c.id} className="rounded-md border p-4">
                <h3 className="font-medium">
                  {c.processo} · {c.nivel_detalhe}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {data(c.periodo_inicio)} a {data(c.periodo_fim)}
                </p>
                <p className="mt-2 break-all text-xs text-muted-foreground">Consulta {c.id}</p>
                <Button
                  className="mt-3 w-full sm:w-auto"
                  variant={c.resultado_id ? 'outline' : 'default'}
                  disabled={enviando || !!c.resultado_id}
                  onClick={() => void verbo('montar_resultado', { consultaId: c.id })}
                >
                  {c.resultado_id ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Resultado persistido
                    </>
                  ) : (
                    <>Montar indicador</>
                  )}
                </Button>
              </article>
            ))
          )}
        </div>
        <div className="min-w-0">
          {!selecionado ? (
            <EmptyState
              icon={FileWarning}
              title="Indicador ainda não montado"
              description="Monte um recorte para fixar corte, consulta, versões e hash reproduzível."
            />
          ) : (
            <article className="rounded-md border p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="font-semibold">Resultado {selecionado.estado}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Corte {instante(selecionado.instante_corte)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  disabled={enviando}
                  onClick={() =>
                    void verbo('recalcular_mesmo_corte', { consultaId: selecionado.consulta_id })
                  }
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Recalcular mesmo corte
                </Button>
              </div>
              {selecionado.estado === 'COMPLETO' ? (
                <p className="mt-5 text-3xl font-semibold" aria-label="Total publicado">
                  {numero(selecionado.total!)}
                </p>
              ) : (
                <Alert className="mt-5">
                  <AlertDescription>
                    Total não publicado. Fontes:{' '}
                    {selecionado.fontes_incompletas.join(', ') || 'divergentes'}.
                  </AlertDescription>
                </Alert>
              )}
              <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
                <Meta t="Soma independente" v={numero(selecionado.soma_independente)} />
                <Meta t="Itens" v={String(selecionado.quantidade_itens)} />
                <Meta t="Consulta executada" v={selecionado.consulta_executada} />
                <Meta t="Versões" v={selecionado.versoes_fontes.join(', ') || 'nenhuma'} />
                <Meta t="Hash reproduzível" v={selecionado.hash_reproduzivel} largo />
              </dl>
              <h3 className="mt-6 font-semibold">Origens permitidas</h3>
              <div className="mt-2 grid gap-2">
                {selecionado.itens.map((i) => (
                  <button
                    key={i.evento_id}
                    className="min-w-0 rounded-md border p-3 text-left hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() =>
                      void verbo('selecionar_origem', {
                        consultaId: selecionado.consulta_id,
                        eventoId: i.evento_id,
                      })
                    }
                  >
                    <span className="block font-medium">
                      {i.tabela_origem} · versão {i.versao_origem}
                    </span>
                    <span className="block break-all text-xs text-muted-foreground">
                      Evento {i.evento_id} · {i.documento_origem}
                    </span>
                  </button>
                ))}
              </div>
            </article>
          )}
        </div>
      </div>
      <Dialog
        open={estado.dialogos.origem.aberto}
        onOpenChange={(v) => {
          if (!v) void verbo('consultar_estado');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Documento e evento de origem</DialogTitle>
            <DialogDescription>
              Detalhe permitido pelo mesmo recorte do indicador.
            </DialogDescription>
          </DialogHeader>
          {estado.origemSelecionada && (
            <dl className="grid gap-3 text-sm">
              <Meta t="Evento" v={estado.origemSelecionada.evento_id} />
              <Meta t="Registro" v={estado.origemSelecionada.registro_origem_id} />
              <Meta t="Documento" v={estado.origemSelecionada.documento_origem} />
              <Meta t="Versão" v={estado.origemSelecionada.versao_origem} />
              <Meta t="Ocorrido em" v={instante(estado.origemSelecionada.ocorrido_em)} />
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
function Meta({ t, v, largo = false }: { t: string; v: string; largo?: boolean }) {
  return (
    <div className={largo ? 'sm:col-span-2' : ''}>
      <dt className="text-muted-foreground">{t}</dt>
      <dd className="break-all font-medium">{v}</dd>
    </div>
  );
}
function data(v: string) {
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(`${v}T00:00:00Z`));
}
function instante(v: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }).format(
    new Date(v),
  );
}
function numero(v: string) {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(Number(v));
}
function aviso(e: unknown) {
  return e instanceof ErroApi ? e.aviso : 'Não foi possível concluir. Tente novamente.';
}
